import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Building2, Contact, DoorOpen, FileText, Megaphone } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { SEARCH_GROUP_LABELS } from '@blimo/shared';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@blimo/ui';

/** Minimum length of the search term (the API answers 422 below). */
export const SEARCH_MIN_LENGTH = 2;
/** Debounce of the search request in ms. */
export const SEARCH_DEBOUNCE_MS = 300;

const GROUP_ICONS = { properties: Building2, units: DoorOpen, parties: Contact, documents: FileText, announcements: Megaphone };

/**
 * Globale Suche (⌘K / Strg+K): GET /admin/search?q= (ab 2 Zeichen, entprellt), Treffer gruppiert nach Bereich – jeder
 * Bereich nur mit passendem Recht (serverseitig). Pfeiltasten/Enter wählen (cmdk), Auswahl navigiert zu `link`.
 * Dokumente haben in der Verwaltung keine eigene Seite: sie öffnen die Inline-Ansicht in einem neuen Tab. Dokumenttreffer
 * im Inhalt zeigen einen Textausschnitt (`snippet`: Segmente `{ text, hit }`, Treffer als <mark> – nie als HTML).
 * @param {{ open: boolean, onOpenChange: (open: boolean) => void }} props
 */
export function GlobalSearch({ open, onOpenChange }) {
  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Globale Suche"
      description="Liegenschaften, Einheiten, Personen, Dokumente und Mitteilungen durchsuchen"
    >
      {open && <SearchContent onClose={() => onOpenChange(false)} />}
    </CommandDialog>
  );
}

function SearchContent({ onClose }) {
  const api = useApi();
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [term, setTerm] = useState('');

  useEffect(() => {
    const next = input.trim();
    const timer = setTimeout(() => setTerm(next), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const active = term.length >= SEARCH_MIN_LENGTH;
  const { data, error, isFetching } = useQuery(adminQueries.search(api, term));
  const groups = active && data ? Object.keys(SEARCH_GROUP_LABELS).filter((key) => data[key]?.length > 0) : [];
  const pending = input.trim() !== term || (active && isFetching && !data);

  /** @param {string} group @param {{ id: number, link: string }} hit */
  function select(group, hit) {
    onClose();
    if (group === 'documents') {
      window.open(api.admin.documents.downloadUrl(hit.id, { inline: true }), '_blank', 'noopener');
      return;
    }
    navigate({ to: hit.link });
  }

  let status = null;
  if (input.trim().length < SEARCH_MIN_LENGTH) status = `Mindestens ${SEARCH_MIN_LENGTH} Zeichen eingeben.`;
  else if (pending) status = 'Suche läuft …';
  else if (error) status = 'Die Suche ist fehlgeschlagen. Bitte erneut versuchen.';

  return (
    <>
      <CommandInput placeholder="Suchbegriff eingeben …" value={input} onValueChange={setInput} />
      <CommandList label="Suchergebnisse">
        {status ? (
          <p role="status" className="py-6 text-center text-sm text-muted-foreground">
            {status}
          </p>
        ) : (
          <CommandEmpty>
            <span role="status">Keine Treffer für „{term}“.</span>
          </CommandEmpty>
        )}
        {!status &&
          groups.map((group) => {
            const Icon = GROUP_ICONS[group];
            return (
              <CommandGroup key={group} heading={SEARCH_GROUP_LABELS[group]}>
                {data[group].map((hit) => (
                  <CommandItem key={`${group}:${hit.id}`} value={`${group}:${hit.id}`} onSelect={() => select(group, hit)}>
                    <Icon aria-hidden="true" className="text-muted-foreground" />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{hit.label || `#${hit.id}`}</span>
                      {hit.hint && <span className="truncate text-xs text-muted-foreground">{hit.hint}</span>}
                      {hit.snippet?.length > 0 && <SearchSnippet segments={hit.snippet} />}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}
      </CommandList>
    </>
  );
}

/**
 * Textausschnitt eines Volltexttreffers; Segmente kommen als reiner Text vom Server (kein HTML → kein XSS).
 * @param {{ segments: { text: string, hit: boolean }[] }} props
 */
export function SearchSnippet({ segments }) {
  return (
    <span className="line-clamp-2 text-xs text-muted-foreground" data-testid="search-snippet">
      {segments.map((s, i) =>
        s.hit ? (
          <mark key={i} className="rounded-sm bg-primary/15 px-0.5 font-medium text-foreground">
            {s.text}
          </mark>
        ) : (
          <span key={i}>{s.text}</span>
        ),
      )}
    </span>
  );
}
